/**
 * A SQL prompt onto the real database, for when the UI is not the shortest way
 * to an answer — what does the schema actually say, why does this row look odd,
 * fix these five records by hand.
 *
 * It goes through the same facade as everything else, so it works on whichever
 * driver is underneath and cannot drift from what the app sees. There is no
 * sandbox: this writes to your data. That is the point of it, and the reason
 * the sheet says so out loud.
 */
import { query, run, flushDatabase } from '../db'

/**
 * Statements that hand back rows rather than change anything. `all()` is the
 * only path that returns rows and `run()` is the only one that persists, so
 * which of the two to call has to be decided from the SQL itself.
 */
const READS = /^\s*(?:select|pragma|explain|with)\b/i

export const isRead = (sql) => READS.test(String(sql ?? ''))

// `query` answers straight away on some drivers and with a promise on others,
// so everything here is async and callers get a promise either way.

/** The tables and views, less the ones SQLite keeps for itself. */
export const listTables = async () =>
  query(
    `SELECT name, type FROM sqlite_master
      WHERE type IN ('table', 'view') AND name NOT LIKE 'sqlite_%'
      ORDER BY type, name`,
  )

// table_xinfo rather than table_info, which leaves generated columns out.
export const describeTable = async (name) => query(`PRAGMA table_xinfo(${name})`)

/** The last statements run, newest first — handy, and never written to disk. */
export const history = []
const HISTORY = 25

function remember(sql) {
  const text = sql.trim()
  const at = history.indexOf(text)
  if (at > -1) history.splice(at, 1)
  history.unshift(text)
  history.length = Math.min(history.length, HISTORY)
}

/**
 * Runs one statement and says what happened.
 *
 * @param {string} sql
 * @returns {Promise<object>} `{ read, columns, rows, changed, ms, error }`
 */
export async function runSql(sql) {
  const text = String(sql ?? '').trim()
  if (!text) return { read: true, columns: [], rows: [], changed: 0, ms: 0, error: '' }
  const read = isRead(text)
  const started = Date.now()
  try {
    if (read) {
      const rows = await query(text)
      remember(text)
      return {
        read,
        // Column order comes from the first row; a query that matched nothing
        // has none to give, which the sheet says rather than guesses at.
        columns: rows.length ? Object.keys(rows[0]) : [],
        rows,
        changed: 0,
        ms: Date.now() - started,
        error: '',
      }
    }
    await run(text)
    // `changes()` is per connection, so it still speaks for the statement just
    // run even though this is a second round trip.
    const [{ changed = 0 } = {}] = await query('SELECT changes() AS changed')
    await flushDatabase()
    remember(text)
    return { read, columns: [], rows: [], changed, ms: Date.now() - started, error: '' }
  } catch (error) {
    return {
      read,
      columns: [],
      rows: [],
      changed: 0,
      ms: Date.now() - started,
      error: error?.message ?? String(error),
    }
  }
}
